"use client";

import { useEffect, useState } from "react";
import type { AiIconVariant } from "@repo/utils/aiDisclaimerIcons";
import { badgeSourceRect, type ObjectFit } from "@repo/utils/aiIconVariant";
import {
  DEFAULT_AI_BADGE_POSITION,
  type AiBadgePosition,
} from "@repo/utils/aiBadgePosition";

/**
 * Resolved variants, keyed by the exact region measured, shared across the page.
 *
 * Module-level rather than per-component: a listing renders the same card image several
 * times, and without this each badge would request an answer another badge already has.
 * `inFlight` deduplicates the first render, where nothing is resolved yet but several
 * components ask at once.
 */
/** How long a resize must settle before the region is measured again. */
const RESIZE_SETTLE_MS = 200;

const resolved = new Map<string, AiIconVariant>();
const inFlight = new Map<string, Promise<AiIconVariant | null>>();

/**
 * The url the browser actually painted, and the part of it under the badge.
 *
 * `currentSrc` rather than the src we were handed: `next/image` and the Storyblok loader
 * rewrite the request, and `srcset` picks a candidate per viewport, so the file on screen
 * is rarely the one the call site named. Measuring anything else measures a picture the
 * reader is not looking at.
 *
 * A `/_next/image` url is refused rather than unwrapped to the upstream it proxies. The
 * rect is measured against `naturalWidth`, which for a proxied image is the size of the
 * RESIZED file the optimizer produced — so handing the route the original would have it
 * crop those coordinates out of a differently-sized picture, silently sampling the wrong
 * region. Both apps configure a `loaderFile`, so nothing here takes that path today; the
 * guard is what keeps that true if the loader is ever removed.
 */
function measure(
  img: HTMLImageElement,
  badge: { width: number; height: number },
  position: AiBadgePosition,
) {
  const raw = img.currentSrc || img.src;

  if (!raw) return null;

  let src: string;

  try {
    const url = new URL(raw, window.location.href);

    if (url.pathname === "/_next/image") return null;

    src = url.href;
  } catch {
    return null;
  }

  const box = img.getBoundingClientRect();
  const style = window.getComputedStyle(img);
  const [rawX, rawY] = style.objectPosition.split(" ");

  const fraction = (value: string | undefined, fallback: number) => {
    if (!value) return fallback;
    if (value.endsWith("%")) return Number.parseFloat(value) / 100;
    // A length (`10px`) cannot be turned into a fraction without the painted size, which
    // is what we are solving for. Centre is the honest default and the CSS default.
    return fallback;
  };

  const rect = badgeSourceRect({
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
    boxWidth: box.width,
    boxHeight: box.height,
    fit: (style.objectFit || "fill") as ObjectFit,
    positionX: fraction(rawX, 0.5),
    positionY: fraction(rawY, 0.5),
    badgeWidth: badge.width,
    badgeHeight: badge.height,
    position,
  });

  if (!rect) return null;

  // Quantised before it becomes a cache key. The rect moves by a pixel or two with every
  // step of a window drag, and an un-rounded key would make each of those a cache miss —
  // a fresh request for an answer that cannot have changed. 8px is far below the scale at
  // which an average tone shifts.
  const quantise = (value: number) => Math.round(value / 8) * 8;

  return {
    src,
    // The size the rect is expressed in, sent with it.
    //
    // `naturalWidth` is the decoded size of whichever `srcset` candidate the browser
    // holds, and `src` names the file the server will crop — and those are not the same
    // picture. The loader delivers `/m/1920x960` while the decoded candidate is 1440x720,
    // so a rect in natural pixels lands at 75% of its true position once the server
    // applies it to the delivered file; at 390px the same mismatch is 61% and the crop
    // misses the badge's corner entirely, picking the artwork that vanishes on it.
    //
    // Sending the space rather than converting here is what makes it robust: the server
    // knows the delivered file's real dimensions from its own bytes, so it can rescale
    // exactly, even when a `srcset` swap changed the candidate mid-measurement.
    natural: { width: img.naturalWidth, height: img.naturalHeight },
    rect: {
      x: quantise(rect.x),
      y: quantise(rect.y),
      width: Math.max(1, quantise(rect.width)),
      height: Math.max(1, quantise(rect.height)),
    },
  };
}

/**
 * The image a badge labels, among everything inside its positioning container.
 *
 * The badge is absolutely positioned against that container, so the picture it covers is
 * in there — but it need not be the only `<img>`: `ImageWithAspectRatio` renders a play
 * icon inside `VideoPlayerButton`, and taking the first in document order can pick that
 * instead. The largest painted area is the one the badge actually sits on; a control's
 * glyph is never the biggest thing in a media container.
 */
function findLabelledImage(container: Element): HTMLImageElement | null {
  const candidates = Array.from(
    container.querySelectorAll<HTMLImageElement>("img:not([data-ai-badge])"),
  );

  let best: HTMLImageElement | null = null;
  let bestArea = 0;

  for (const candidate of candidates) {
    const box = candidate.getBoundingClientRect();
    const area = box.width * box.height;

    if (area > bestArea) {
      best = candidate;
      bestArea = area;
    }
  }

  // A video the badge actually sits on has nothing measurable: its frames are not
  // readable from here. Compared by painted area rather than by presence, because both
  // shapes occur — a video hero whose only <img> is a play-button glyph (measuring that
  // would judge the background from an icon), and an image beside a small decorative
  // video, which must still be measured.
  const videoArea = Math.max(
    0,
    ...Array.from(container.querySelectorAll("video")).map((video) => {
      const box = video.getBoundingClientRect();
      return box.width * box.height;
    }),
  );

  if (videoArea > bestArea) return null;

  // Nothing is painted yet on the first pass — no layout, or images still loading — so
  // fall back to document order rather than giving up on measuring at all.
  return best ?? candidates[0] ?? null;
}

async function fetchVariant(
  src: string,
  rect: { x: number; y: number; width: number; height: number },
  natural: { width: number; height: number },
): Promise<AiIconVariant | null> {
  try {
    const params = new URLSearchParams({
      src,
      x: String(Math.round(rect.x)),
      y: String(Math.round(rect.y)),
      w: String(Math.round(rect.width)),
      h: String(Math.round(rect.height)),
      // The pixel space `rect` is in. Without it the server has no way to know the rect
      // describes a different-sized copy of the same picture.
      nw: String(Math.round(natural.width)),
      nh: String(Math.round(natural.height)),
    });
    const response = await fetch(`/api/ai-badge-variant?${params}`);

    if (!response.ok) {
      // Logged rather than swallowed. The badge degrades to its default artwork on any
      // failure, which is silent by design — so without this a route broken site-wide
      // looks exactly like a site where every image happens to be light.
      console.warn(
        `[ai-badge] variant lookup failed (${response.status}) for ${src}`,
      );
      return null;
    }

    const body = (await response.json()) as { variant?: AiIconVariant };

    return body.variant ?? null;
  } catch (error) {
    console.warn(`[ai-badge] variant lookup errored for ${src}`, error);
    return null;
  }
}

/**
 * The badge artwork that will be visible, or `null` until that is known.
 *
 * Takes the badge's own element and finds the image it sits over, rather than a url from
 * the call site: only the DOM knows the painted size, the crop and the file the browser
 * actually chose, and all three decide which pixels are behind the badge.
 *
 * Returning `null` rather than a guess lets the caller render its default immediately —
 * the badge is a compliance artifact and must never wait on a measurement to appear.
 */
export function useAiBadgeVariant(
  node: HTMLElement | null,
  enabled: boolean,
  badgeWidth: number,
  badgeHeight: number,
  position: AiBadgePosition = DEFAULT_AI_BADGE_POSITION,
): AiIconVariant | null {
  const [variant, setVariant] = useState<AiIconVariant | null>(null);

  useEffect(() => {
    // Any input to the measurement changing invalidates the answer, and `position` is
    // one of them: the badge would otherwise wear the verdict measured for the corner it
    // has just left until a fresh one lands.
    setVariant(null);

    if (!enabled || !node) return;

    const container = node.parentElement;

    if (!container) return;

    let active = true;
    let watched: HTMLImageElement | null = null;

    const run = () => {
      const img = findLabelledImage(container);

      // Cleared, not left as it was. The badge outlives the media under it — a carousel
      // swaps an image for a video without unmounting it — and a stale verdict would
      // keep the artwork chosen for a picture that is no longer there.
      if (!img) {
        setVariant(null);
        return;
      }

      // Re-resolved on every run rather than captured once: a carousel swaps the `<img>`
      // under a badge that never unmounts, and a node captured at mount would leave the
      // badge showing a verdict measured from a picture that is no longer there.
      if (img !== watched) {
        watched?.removeEventListener("load", run);
        watched = img;
      }

      // Checked on every run, not only when the element changes.
      //
      // An `<img>` whose `src` is swapped in place keeps the previous decoded bitmap — and
      // with it the previous `naturalWidth` — until the new file loads. A run triggered by
      // that mutation would measure the old picture, cache the verdict under the old key
      // and never be asked again. Waiting for `load` makes the swap self-correcting.
      //
      // No component in either app is known to do this today; the observer below watches
      // `src`/`srcset` because the swap is an anticipated input, and the guard costs a
      // property read. Hardening, not a fix for an observed failure.
      if (!(img.complete && img.naturalWidth > 0)) {
        img.removeEventListener("load", run);
        img.addEventListener("load", run, { once: true });
        return;
      }

      const measured = measure(
        img,
        { width: badgeWidth, height: badgeHeight },
        position,
      );

      if (!measured || !active) return;

      // The natural size is part of the key: the same rect against a different candidate
      // is a different region of the picture, and reusing one answer for the other is how
      // a resize could pin the verdict from a crop that is no longer on screen.
      const key = `${measured.src}|${Math.round(measured.rect.x)},${Math.round(
        measured.rect.y,
      )},${Math.round(measured.rect.width)},${Math.round(
        measured.rect.height,
      )}@${measured.natural.width}x${measured.natural.height}`;

      const cached = resolved.get(key);

      if (cached) {
        setVariant(cached);
        return;
      }

      const pending =
        inFlight.get(key) ??
        (() => {
          const promise = fetchVariant(
            measured.src,
            measured.rect,
            measured.natural,
          ).then((result) => {
            if (result) resolved.set(key, result);
            inFlight.delete(key);
            return result;
          });

          inFlight.set(key, promise);
          return promise;
        })();

      void pending.then((result) => {
        if (active && result) setVariant(result);
      });
    };

    // `run` waits for the image it resolves to have a natural size, so a measurement is
    // never taken before there is anything to measure.
    run();

    // Every call site renders `fill` + `object-cover` with a `sizes` hint, so a resize
    // changes both the painted box and the `srcset` candidate — and with them the region
    // under the badge. Without this the badge keeps a verdict computed for a crop that is
    // no longer on screen.
    //
    // Debounced, because ResizeObserver fires for every frame of a window drag and each
    // distinct rect costs a request against a route that now fetches the delivered image.
    // Settling first turns a drag into one measurement instead of dozens.
    let settle: ReturnType<typeof setTimeout> | undefined;

    const onResize = () => {
      clearTimeout(settle);
      settle = setTimeout(run, RESIZE_SETTLE_MS);
    };

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(onResize);

    // The container, not the image: it outlives any single `<img>` inside it, so the
    // observation survives a carousel swapping the picture out.
    observer?.observe(container);

    // A swap changes neither the container's size nor the badge's props, so a resize
    // alone would not notice it. Debounced through the same settle as a resize — a
    // slideshow transition mutates the tree repeatedly on its way to one final state.
    const mutations =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver((records) => {
            // The badge swaps its own `src` the moment a variant resolves, and that is a
            // mutation inside the tree being watched. Re-running on it is harmless — the
            // key is already resolved — but ignoring our own writes keeps the observer
            // from answering itself.
            if (records.every((record) => node.contains(record.target))) return;

            onResize();
          });

    mutations?.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src", "srcset"],
    });

    return () => {
      active = false;
      clearTimeout(settle);
      watched?.removeEventListener("load", run);
      observer?.disconnect();
      mutations?.disconnect();
    };
    // `position` is a dependency, not a captured constant: moving the badge moves the
    // region whose contrast decides its artwork, and a stale measurement would leave the
    // new corner wearing the verdict from the old one.
  }, [node, enabled, badgeWidth, badgeHeight, position]);

  return variant;
}
